import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import Axios from "axios";
import Localizer from "@Scripts/Common/Localizer";
import GoogleMapsHelper from "@Scripts/Infrastructure/GoogleMapsHelper";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";

@Component({ name: "Locator" })
export default class Locator extends Vue {
    public localizer: any = Localizer;
    public googleMapsHelper: any = GoogleMapsHelper;
    public isLoading: boolean = false;
    public isCreated: boolean = true;
    public iShowmap: boolean = false;
    public geoMarkerFilter: any = {
        topRightLatitude: null,
        topRightLongitude: null,
        bottomLeftLatitude: null,
        bottomLeftLongitude: null
    };

    public geoMarkerMarkerTypes: any[] = [];
    public geoMarkers: any[] = [];
    public mapErrors: any[] = [];

    public suggestionIndex: number = -1;
    public suggestions: any[] = [];
    public searchtext: string = null;
    public debounceTimeOut: any = null;

    @Prop() readonly language: string;
    @Prop() readonly region: string;

    get routes() {
        return (<any>window).App.routes;
    }

    public async created() {
        const self = this;
        this.isLoading = true;

        await Promise.all([this.googleMapsHelper.loadMap(), this.getData()]);

        this.googleMapsHelper.mapInstance.addListener("tilesloaded", async () => {
            do {
                await self.getGeoMarkers();
            } while (self.isCreated && self.geoMarkers.length === 0);
        });

        this.isLoading = false;
    }

    public mounted() {
        const outsideClickListener = event => {
            if (!document.getElementById("AutocompleteLocation")?.contains(event.target)) {
                this.suggestions = [];
                this.suggestionIndex = -1;
            }
        }

        document.addEventListener("click", outsideClickListener);
    }

    public async getData() {
        const response = (await Axios.post(this.routes.Support_GetGeoMarkerMarkerTypes)).data;
        this.geoMarkerMarkerTypes = response;
    }

    public async getGeoMarkers() {
        let mapBounds = this.googleMapsHelper.getBounds();

        let nePoint = mapBounds.getNorthEast();
        let swPoint = mapBounds.getSouthWest();

        this.geoMarkerFilter.topRightLatitude = nePoint.lat();
        this.geoMarkerFilter.topRightLongitude = nePoint.lng();
        this.geoMarkerFilter.bottomLeftLatitude = swPoint.lat();
        this.geoMarkerFilter.bottomLeftLongitude = swPoint.lng();

        const response = (await Axios.post(this.routes.Support_GetGeoMarkers, this.geoMarkerFilter)).data;
        if (this.isCreated) {
            if (response && response.length !== 0) {
                this.isCreated = false;
            } else if (this.googleMapsHelper.mapInstance.zoom !== this.googleMapsHelper.mapInstance.minZoom) {
                this.googleMapsHelper.mapInstance.setZoom(this.googleMapsHelper.mapInstance.zoom - 1);
            } else if (this.googleMapsHelper.mapInstance.zoom === this.googleMapsHelper.mapInstance.minZoom) {
                this.isCreated = false;
            }
        }

        this.googleMapsHelper.removeMarkers();
        this.geoMarkers = response;

        for (const item of this.geoMarkers) {
            let description = "<div class=\"markerinfo\"><div class=\"maintitle s20 color-cyan50\">" + item.company + "</div><div class=\"type\">" + (item.description ? item.description : this.geoMarkerMarkerTypes.find(x => x.value == item.geoMarkerType)?.title) + "</div><div class=\"address\">" + item.address + " " + item.location + " " + item.postCode + ", " + item.zone + " (" + item.country + ")</div>" + "<div class=\"contacts\">";

            if (item.phone) {
                description += "<a href=\"tel:" + item.phone + "\" class=\"link\"><span class=\"icon fastic-phone\"></span><span class=\"label\">" + item.phone + "</span></a>";
            }
            if (item.websiteUrl) {
                description += "<a href=\"" + item.websiteUrl + "\" target=\"_blank\" rel=\"noreferrer\" class=\"link\"><span class=\"icon fastic-globe\"></span><span class=\"label\">" + this.localizer.get("App.Common.WebSite") + "</span></a>";
            }
            if (item.email) {
                description += "<a href=\"mailto:" + item.email + "\" class=\"link\"><span class=\"icon fastic-email\"></span><span class=\"label\">" + this.localizer.get("App.Common.Email") + "</span></a>";
            }

            description += "<a href=\"https://www.google.com/maps/search/?api=1&query=" + item.company + " - " + item.address + " - " + item.postCode + " " + item.location + ", " + item.zone + " (" + item.country + ")\" target=\"_blank\" rel=\"noreferrer\" class=\"link\"><span class=\"icon fastic-locator\"></span><span class=\"label\">" + this.localizer.get("App.Common.Direction") + "</span></a>";
            description += "</div></div>";

            this.googleMapsHelper.addMarker(item.idMaster, item.latitude, item.longitude, item.type, item.company, description);
        }
    }

    public onSearchtextChanged() {
        this.debounceSearchtext(500);
    }

    public async centerToUser() {
        const self = this;

        navigator.geolocation.getCurrentPosition(function (result) {
            self.googleMapsHelper.setActiveInfoWindowId(null);
            self.googleMapsHelper.setCenter(result.coords.latitude, result.coords.longitude);
            self.googleMapsHelper.mapInstance.setZoom(15);
        }, function () { }, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
        });
    }

    public centerMarker(geoMarker: any) {
        this.googleMapsHelper.setActiveInfoWindowId(geoMarker.idMaster);
        this.googleMapsHelper.setCenter(geoMarker.latitude, geoMarker.longitude);
        this.googleMapsHelper.mapInstance.setZoom(15);
        this.googleMapsHelper.clickMarker(geoMarker.idMaster);
    }

    public async debounceSearchtext(delay: number) {
        const self = this;
        clearTimeout(this.debounceTimeOut);
        this.debounceTimeOut = setTimeout(async function (context) {
            if (self.searchtext && self.searchtext.length >= 3) {
                await self.getSuggestions();
            } else {
                self.suggestions = [];
                self.suggestionIndex = -1;
            }
        }, delay, this);
    }

    public async getSuggestions() {
        const self = this;

        let autocompleteService = this.googleMapsHelper.getAutocompleteService();
        autocompleteService.getPlacePredictions({ input: this.searchtext, language: this.language, types: ["locality", "postal_code", "administrative_area_level_1", "administrative_area_level_2"] }, async function (suggestions) {
            let placeSuggestions: any[] = [];

            if (suggestions) {
                placeSuggestions = suggestions.map(x => ({
                    description: x.description,
                    address: x.description,
                    type: "Place"
                }));
            }

            const response = (await Axios.post(self.routes.Support_GetSuggestionPlace, {
                searchtext: self.searchtext,
                market: self.geoMarkerFilter.market,
                types: self.geoMarkerFilter.types
            })).data;

            self.suggestions = [...placeSuggestions, ...response];
            self.suggestions = self.suggestions.map((obj, index) => ({ ...obj, index }));
        });
    }

    public async searchPlace() {
        if (this.searchtext?.length >= 3) {
            this.resetSuggestionValues();
            await this.getSuggestions();
        }
    }

    public resetSuggestionValues() {
        this.suggestionIndex = -1;
        this.suggestions = [];
    }

    public resetSearchtext() {
        this.searchtext = null;
        this.resetSuggestionValues();
    }

    public suggestionMoveDown() {
        if (this.suggestions.length !== 0 && this.suggestionIndex < this.suggestions.length - 1) {
            this.suggestionIndex++;
        }
    }

    public suggestionMoveUp() {
        if (this.suggestions.length !== 0 && this.suggestionIndex > 0) {
            this.suggestionIndex--;
        }
    }

    public async selectSuggestion(suggestion: any) {
        const self = this;
        this.mapErrors = [];

        if (suggestion.type === "Place") {
            this.googleMapsHelper.setActiveInfoWindowId(null);

            let geocoder = this.googleMapsHelper.getGeocoder();
            geocoder.geocode({ address: suggestion.address }, async function (data, status) {
                if (status === (<any>window).google.maps.GeocoderStatus.OK && data) {
                    let place = data[0];

                    if (place && !place.geometry) {
                        self.mapErrors.push(self.localizer.get("App.Common.AddressNotFound"));
                        setTimeout(function () { self.mapErrors = []; }, 3000);
                        return;
                    } else {
                        let location = place.geometry.location.toJSON();

                        self.googleMapsHelper.mapInstance.setZoom(11);
                        self.googleMapsHelper.setCenter(location.lat, location.lng);

                        self.searchtext = place.formatted_address;
                    }
                }
            });
        } else {
            this.setMarker(suggestion);
        }

        this.resetSuggestionValues();
    }

    public setPlace() {
        if (this.suggestionIndex !== -1) {
            const suggestion = this.suggestions[this.suggestionIndex];
            if (suggestion.type !== "Place") {
                this.setMarker(suggestion);
            } else {
                this.selectSuggestion(suggestion);
            }
        }
    }

    public setMarker(marker: any) {
        this.centerMarker(marker);
        this.searchtext = marker.company;
        this.resetSuggestionValues();
    }

    public setAnalytics(name: string, cta: string) {
        if (this.region === "region") {
            GTagHelper.sendCustomEvent("click", { "region_locator": name + "_" + cta });
        } else {
            GTagHelper.sendCustomEvent("click", { "locator": name + "_" + cta });
        }
    }

    public setShowMap() {
        this.iShowmap = !this.iShowmap;
    }
};